import React, { useState, useCallback } from "react";
import { database } from "../../../firebaseConfig";
import { get, ref } from "firebase/database";
import toast from "react-hot-toast";

const MODE_LABELS = { kcb: "KCB", mpesa: "M-Pesa", all: "All" };

const matchesPaymentMode = (modeValue, selectedMode) => {
  if (!selectedMode || selectedMode === "all") return true;
  const normalized = String(modeValue || "").toLowerCase();
  if (selectedMode === "kcb") {
    return normalized === "kcb";
  }
  // Treat any mpesa variant (m-pesa, mpesa, m pesa) as a match
  return normalized.replace(/[\s-]/g, "") === "mpesa";
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const sanitizeFilename = (name) => {
  if (!name) return "farmers";
  return name.replace(/[^a-z0-9_\-]/gi, "_").replace(/_+/g, "_");
};

const sanitizeForFirebasePath = (value) =>
  value ? value.replace(/[.#$[\]/]/g, "_") : "";

const DownloadFarmersModal = ({ open, onClose, companyName }) => {
  const [selectedMode, setSelectedMode] = useState("all");
  const [downloading, setDownloading] = useState(false);

  const handleBackdropClick = useCallback(() => {
    if (!downloading) {
      onClose();
    }
  }, [downloading, onClose]);

  const buildCsvAndDownload = useCallback(
    (farmers) => {
      const header = ["Cloud Name", "Phone Number", "Identification Number", "Address"];
      const rows = farmers.map((farmer) => [
        farmer.CloudName || "",
        farmer.PhoneNumber || "",
        farmer.IdentificationNumber || "",
        farmer.Address || "",
      ]);

      const csvContent = [header, ...rows].map((r) => r.map(escapeCsvValue).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const friendlyMode = MODE_LABELS[selectedMode] || selectedMode;
      const filename = `${sanitizeFilename(companyName)}_farmers_${sanitizeFilename(friendlyMode)}.csv`;
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Farmers CSV generated");
    },
    [companyName, selectedMode]
  );

  const handleDownload = useCallback(async () => {
    if (!companyName) {
      toast.error("Company name not loaded yet");
      return;
    }
    if (!database) {
      toast.error("Database connection unavailable");
      return;
    }

    setDownloading(true);
    try {
      const sanitizedCompany = sanitizeForFirebasePath(companyName);
      const farmersRef = ref(database, `Companies/${sanitizedCompany}/ValidatedFarmers`);
      const snapshot = await get(farmersRef);

      if (!snapshot.exists()) {
        toast.error("No farmers available to download");
        return;
      }

      let farmers = Object.values(snapshot.val() || {});
      farmers = farmers.filter((farmer) => matchesPaymentMode(farmer.Mode, selectedMode));

      if (farmers.length === 0) {
        toast.error("No farmers match the selected list");
        return;
      }

      farmers.sort((a, b) => {
        const nameA = String(a.CloudName || "").toLowerCase();
        const nameB = String(b.CloudName || "").toLowerCase();
        return nameA.localeCompare(nameB, "en", { sensitivity: "base" });
      });

      buildCsvAndDownload(farmers);
      onClose();
    } catch (error) {
      console.error("Error downloading farmers CSV:", error);
      toast.error("Failed to download farmers list");
    } finally {
      setDownloading(false);
    }
  }, [buildCsvAndDownload, companyName, onClose, selectedMode]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      style={{ cursor: downloading ? "not-allowed" : "default" }}
    >
      <div
        className="bg-white rounded-xl p-6 shadow-xl w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-cyan-700 mb-2">
          Download Farmers CSV
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Choose the list to download. Files include Cloud Name, Phone Number, Identification Number, and Address.
        </p>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              value="kcb"
              checked={selectedMode === "kcb"}
              onChange={(e) => setSelectedMode(e.target.value)}
              disabled={downloading}
              className="text-cyan-600 focus:ring-cyan-600"
            />
            <span className="text-gray-700">KCB list</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              value="mpesa"
              checked={selectedMode === "mpesa"}
              onChange={(e) => setSelectedMode(e.target.value)}
              disabled={downloading}
              className="text-cyan-600 focus:ring-cyan-600"
            />
            <span className="text-gray-700">M-Pesa list</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              value="all"
              checked={selectedMode === "all"}
              onChange={(e) => setSelectedMode(e.target.value)}
              disabled={downloading}
              className="text-cyan-600 focus:ring-cyan-600"
            />
            <span className="text-gray-700">All farmers</span>
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={downloading}
            className="px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className={`px-4 py-2 rounded text-white transition ${
              downloading ? "bg-cyan-300 cursor-not-allowed" : "bg-cyan-600 hover:bg-cyan-700"
            }`}
          >
            {downloading ? "Preparing..." : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadFarmersModal;
